import { ComponentType, ErrorInfo, PropsWithChildren } from 'react'
import { ErrorBoundary, withErrorBoundary } from 'react-error-boundary'
import { getSupportEmail } from '../../core/globals'
import { ErrorLevel } from '../constants'
import { emitError, PlatformError } from '../utils'
import { ErrorAccumulatorProvider } from './error-accumulator'
import { ErrorFallback, ErrorFallbackProps } from './error-fallback'
import type { PlatformFallbackProps } from '../types'

export interface PlatformErrorBoundaryProps extends Pick<
  ErrorFallbackProps,
  'level'
> {
  message?: string
  onError?: (error: PlatformError, info: ErrorInfo) => void
}

/**
 * Convenience component returning a fixed-level error boundary with fallback.
 * Automatically emits error events to the event bus. Accepts an error callback
 * to extend further side effects if needed.
 */
export const PlatformErrorBoundary = ({
  children,
  level = ErrorLevel.COMPONENT,
  message,
  onError,
}: PropsWithChildren<PlatformErrorBoundaryProps>) => {
  const handleError = (...args: Parameters<NonNullable<typeof onError>>) => {
    emitError(...args, level)
    onError?.(...args)
  }

  const boundary = (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore -- But it is valid :'(
    <ErrorBoundary
      onError={handleError}
      FallbackComponent={({
        error,
        resetErrorBoundary,
      }: PlatformFallbackProps) => (
        <ErrorFallback
          level={level}
          error={error}
          reset={resetErrorBoundary}
          code={error.code}
          traceId={error.traceId}
          supportEmail={getSupportEmail() ?? undefined}
        >
          {message}
        </ErrorFallback>
      )}
    >
      {children}
    </ErrorBoundary>
  )

  if (level === ErrorLevel.TEMPLATE) {
    return <ErrorAccumulatorProvider>{boundary}</ErrorAccumulatorProvider>
  }

  return boundary
}

/**
 * Component factory returning PlatformErrorBoundary presets
 */
export const makePlatformErrorBoundary =
  (level: ErrorLevel) =>
  ({
    children,
    onError,
  }: PropsWithChildren<Omit<PlatformErrorBoundaryProps, 'level'>>) => {
    return (
      <PlatformErrorBoundary level={level} onError={onError}>
        {children}
      </PlatformErrorBoundary>
    )
  }

export const ObjectErrorBoundary = makePlatformErrorBoundary(ErrorLevel.OBJECT)
export const GroupErrorBoundary = makePlatformErrorBoundary(ErrorLevel.GROUP)
export const ComponentErrorBoundary = makePlatformErrorBoundary(
  ErrorLevel.COMPONENT,
)

/**
 * Convenience Higher Order Component returning a fixed-level error boundary HOC
 * with fallback. Automatically emits error events to the event bus. Accepts an
 * error callback to extend further side effects if needed.
 */
export const withPlatformErrorBoundary = <Props,>(
  Component: ComponentType<Props>,
  {
    level = ErrorLevel.COMPONENT,
    message,
    onError,
  }: PlatformErrorBoundaryProps,
) => {
  const handleError = (...args: Parameters<NonNullable<typeof onError>>) => {
    emitError(...args, level)
    onError?.(...args)
  }

  return withErrorBoundary<Props>(Component, {
    FallbackComponent: ({
      error,
      resetErrorBoundary,
    }: PlatformFallbackProps) => (
      <ErrorFallback
        level={level}
        error={error}
        reset={resetErrorBoundary}
        code={error.code}
        traceId={error.traceId}
        supportEmail={getSupportEmail() ?? undefined}
      >
        {message}
      </ErrorFallback>
    ),
    onError: handleError,
  })
}

/**
 * Higher Order Component factory returning withPlatformErrorBoundary presets
 */
export const makePlatformErrorBoundaryHOC =
  (level: ErrorLevel) =>
  <Props,>(
    Component: ComponentType<Props>,
    options?: Omit<PlatformErrorBoundaryProps, 'level'>,
  ) =>
    withPlatformErrorBoundary<Props>(Component, { ...options, level })

export const withObjectErrorBoundary = makePlatformErrorBoundaryHOC(
  ErrorLevel.OBJECT,
)
export const withGroupErrorBoundary = makePlatformErrorBoundaryHOC(
  ErrorLevel.GROUP,
)
export const withComponentErrorBoundary = makePlatformErrorBoundaryHOC(
  ErrorLevel.COMPONENT,
)
